import React from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ArrowRight, CheckCircle, Users, Clock, Target, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useParams } from 'react-router-dom';
import { EnquiryForm } from '../components/forms/EnquiryForm';

const servicesData = {
  'recrutement-permanent': {
    title: 'Recrutement Permanent',
    subtitle: 'Trouvez les talents qui transformeront votre entreprise',
    description: 'Notre service de recrutement permanent vous permet de trouver les meilleurs candidats pour vos postes clés. Nous combinons expertise sectorielle, méthodologie éprouvée et réseau étendu pour identifier les talents qui correspondent parfaitement à vos besoins.',
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1600&q=80',
    features: [
      'Analyse approfondie de vos besoins et de votre culture d\'entreprise',
      'Sourcing multicanal et chasse de têtes',
      'Évaluation rigoureuse des compétences techniques et comportementales',
      'Présentation de shortlists qualifiées',
      'Accompagnement dans les négociations',
      'Suivi post-intégration'
    ],
    stats: [
      { icon: Users, value: '10,000+', label: 'Placements réalisés' },
      { icon: Clock, value: '3-4', label: 'Semaines en moyenne' },
      { icon: Target, value: '95%', label: 'Taux de satisfaction' },
      { icon: Award, value: '12', label: 'Mois de garantie' }
    ]
  },
  'management-transition': {
    title: 'Management de Transition',
    subtitle: 'Des leaders expérimentés pour vos projets critiques',
    description: 'Notre service de management de transition met à votre disposition des dirigeants et managers expérimentés pour des missions temporaires. Qu\'il s\'agisse de piloter un projet de transformation, de gérer une crise ou de remplacer un cadre clé, nos managers de transition sont opérationnels immédiatement.',
    image: 'https://images.unsplash.com/photo-1664464683525-29b5d442af54?w=1600&q=80',
    features: [
      'Vivier de managers de transition qualifiés et disponibles',
      'Intervention rapide sous 48-72 heures',
      'Expérience avérée dans des contextes exigeants',
      'Flexibilité contractuelle (temps plein ou partiel)',
      'Transfert de compétences à vos équipes',
      'Suivi régulier de la mission'
    ],
    stats: [
      { icon: Users, value: '500+', label: 'Missions réalisées' },
      { icon: Clock, value: '48h', label: 'Délai d\'intervention' },
      { icon: Target, value: '98%', label: 'Missions réussies' },
      { icon: Award, value: '15+', label: 'Années d\'expérience moyenne' }
    ]
  },
  'rpo': {
    title: 'RPO - Recruitment Process Outsourcing',
    subtitle: 'Externalisez votre recrutement pour plus d\'efficacité',
    description: 'Notre solution RPO vous permet d\'externaliser tout ou partie de votre processus de recrutement. Nous nous intégrons à votre équipe RH pour apporter expertise, ressources et technologies, tout en respectant votre marque employeur et vos processus.',
    image: 'https://images.unsplash.com/photo-1758518730384-be3d205838e8?w=1600&q=80',
    features: [
      'Solutions sur mesure adaptées à vos besoins',
      'Intégration complète à vos équipes et outils',
      'Réduction des coûts de recrutement de 20 à 40%',
      'Amélioration du time-to-hire',
      'Reporting et analytics avancés',
      'Évolutivité selon vos volumes'
    ],
    stats: [
      { icon: Users, value: '50+', label: 'Clients RPO actifs' },
      { icon: Clock, value: '-35%', label: 'Time-to-hire' },
      { icon: Target, value: '-30%', label: 'Coût par embauche' },
      { icon: Award, value: '100%', label: 'Conformité RGPD' }
    ]
  },
  'market-intelligence': {
    title: 'Market Intelligence',
    subtitle: 'Des données pour des décisions éclairées',
    description: 'Notre service Market Intelligence vous fournit les insights nécessaires pour prendre des décisions stratégiques en matière de talent acquisition. Études de rémunération, cartographie des talents, analyse de marché : nous vous donnons une vision claire de votre environnement.',
    image: 'https://images.unsplash.com/photo-1573649647330-b3a1375227fe?w=1600&q=80',
    features: [
      'Études de rémunération personnalisées',
      'Cartographie des talents par secteur',
      'Benchmarking concurrentiel',
      'Analyse de votre marque employeur',
      'Conseils stratégiques RH',
      'Rapports sur mesure'
    ],
    stats: [
      { icon: Users, value: '200+', label: 'Études réalisées/an' },
      { icon: Clock, value: '2-3', label: 'Semaines de livraison' },
      { icon: Target, value: '50+', label: 'Secteurs couverts' },
      { icon: Award, value: '10+', label: 'Années de données' }
    ]
  }
};

const ServicePage = () => {
  const { slug } = useParams();
  const service = servicesData[slug] || servicesData['recrutement-permanent'];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <ResponsiveImage loading="eager"
            src={service.image}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-4">{service.title}</h1>
          <p className="text-xl opacity-90 max-w-2xl">{service.subtitle}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#E30022] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {service.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="h-8 w-8 mx-auto mb-3" />
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="opacity-90 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-serif text-gray-800 mb-6">Notre approche</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">{service.description}</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Ce que nous offrons</h3>
              <ul className="space-y-3">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#E30022] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-serif text-gray-800 mb-6">Demandez un devis</h3>
              <EnquiryForm key={slug} kind="service" service={servicesData[slug] ? slug : 'recrutement-permanent'} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Prêt à démarrer ?</h2>
          <p className="text-gray-400 mb-8">Contactez-nous pour discuter de vos besoins spécifiques.</p>
          <a 
            href="/recrutement"
            className="inline-flex items-center gap-2 bg-[#E30022] text-white px-8 py-4 rounded hover:bg-[#c7001d] transition-colors font-medium group"
          >
            Nous contacter
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
